import clsx from "clsx";
import React, { FC, useState } from "react";
import { Button } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import ProofDocumentControl from "~/components/Forms/ViolationRecordingForm/ProofDocumentControl";
import IconDownload from "~/components/Icons/icon-download";
import IconTrash from "~/components/Icons/icon-trash";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { uploadFile } from "~/libs/uploadFile";
import { useSetDealerReportAccountingMutation } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useNotify } from "~/shared/hooks/useNotify";
import { Field } from "~/shared/ui/Field";

import s from "./UsersForm.less";

interface IProps {
  setCloseModal: () => void;
  Files?: any[];
  status?: string;
  isResolved?: boolean;
  refetch;
}

const AccountingReportFilesForm: FC<IProps> = ({
  setCloseModal,
  refetch,
  Files,
  status = "",
  isResolved = false,
}) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const setData = useSetLocationData();

  const [fileId, setFileId] = useState([]);

  const canCheck = hasPermission("accounting.setDealerReport.check");

  const [uploadFileLoader, setUploadFileLoader] = useState(false);
  const [filesOnDelete, setFilesOnDelete] = useState([]);
  const { currentId } = useGetLocationData();
  const { handleSubmit, control, errors } = useForm({});

  const isStatus = status === "checking" || status === "verified";

  const isFiles = fileId.length > 0;

  const { notifySuccess, notifyError } = useNotify();

  const [addValues, { loading: saving }] = useSetDealerReportAccountingMutation(
    {
      onCompleted() {
        setData(
          "openModal",
          "",
          "currentId",
          "",
          "sortField",
          "",
          "sortSubField",
          "",
        );
        refetch();
      },
      onError({ graphQLErrors }) {
        notifyError(graphQLErrors[0].message);
      },
    },
  );

  const selectToDelete = (id) => {
    setFilesOnDelete((p) => {
      if (p.includes(id)) {
        return p.filter((el) => id !== el);
      } else {
        return [...p, id];
      }
    });
  };

  const imageAdd = () => {
    const arrAll =
      Files.length > 0
        ? Files.filter(({ id }) => !filesOnDelete.includes(id))
        : [];

    const fileAll = arrAll.length > 0 ? arrAll.map(({ id }) => id) : [];

    addValues({
      variables: {
        ReportId: currentId,
        FileIds:
          isFiles || filesOnDelete.length > 0
            ? [...fileAll, ...fileId]
            : undefined,
      },
    });
  };

  return (
    <div className={s.ind_form}>
      <ul className={s.list}>
        <form className={s.form_comment} onSubmit={handleSubmit(imageAdd)}>
          <div className={s.form_wrapper}>
            {Files && (
              <div className={s.wrapper_files}>
                <div className={s.title_files}>
                  {t("accounting.planReportScan")}
                </div>
                <div className={s.items_comments}>
                  {Files.map(({ name, link, id }) => (
                    <div
                      key={id}
                      className={clsx(
                        filesOnDelete.includes(id) && s.onDelete,
                        s.wrapper,
                        s.wrapperPlan,
                      )}
                    >
                      <div className={clsx(s.filePreview, s.filePreviewPlan)}>
                        {name}
                      </div>
                      <div className={clsx(s.fileControls, s.fileControlsPlan)}>
                        <a href={link} download className={s.customBtn}>
                          <IconDownload fill={"#878E94"} />
                        </a>
                        {canCheck && !isResolved && !isStatus && (
                          <button
                            type={"button"}
                            className={s.customBtn}
                            onClick={() => selectToDelete(id)}
                          >
                            <IconTrash fill={"#878E94"} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {canCheck && !isStatus && (
              <Field
                type="file"
                name="reportFile"
                control={control}
                label={t("accounting.planReportScan")}
                error={errors["reportFile"]?.message}
                accept={["image/jpeg", "application/pdf"]}
                maxSizeMb={10}
                loading={uploadFileLoader}
                className={s.fileField}
                onUpload={async (file) => {
                  setUploadFileLoader(true);
                  try {
                    const uploaded = await uploadFile(file, () =>
                      notifyError(t("common.data_uploaded_error")),
                    );
                    if (uploaded) {
                      // Файлов может быть несколько: id уходит в общий список,
                      // а onUpload ничего не возвращает — значение поля остаётся
                      // null, и FileField снова показывает кнопку загрузки.
                      setFileId((old) => [...old, uploaded.id]);
                      notifySuccess(t("common.file_uploaded_successfully"));
                    }
                  } finally {
                    setUploadFileLoader(false);
                  }
                }}
              />
            )}
          </div>
          {canCheck && (
            <>
              {isFiles && (
                <div className={s.wrapper_files}>
                  <div className={s.title_files}>
                    {t("accounting.uploaded_files")}
                  </div>
                  <div className={s.items_comments}>
                    {fileId.map((id) => (
                      <ProofDocumentControl
                        key={id}
                        isPlanReport={true}
                        filesId={fileId}
                        setFilesId={setFileId}
                        id={id}
                      />
                    ))}
                  </div>
                </div>
              )}

              {(!!filesOnDelete.length || isFiles) && !isStatus && (
                <Flex justify="flex-end" className={s.actionButton}>
                  <Button htmlType="submit" loading={saving}>
                    {t("ui.save")}
                  </Button>
                </Flex>
              )}
            </>
          )}
        </form>
      </ul>
    </div>
  );
};
export default AccountingReportFilesForm;
